import React from 'react';
import { cva } from 'class-variance-authority';
import { cn } from '../../lib/utils';
import { FaInbox } from 'react-icons/fa';
import { Button } from './Button';

/**
 * EmptyState variants using CVA
 */
export const emptyStateVariants = cva(
  'flex flex-col items-center justify-center text-center',
  {
    variants: {
      size: {
        sm: 'py-8 gap-2',
        md: 'py-12 gap-4',
        lg: 'py-16 gap-6',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  }
);

/**
 * EmptyState Component
 *
 * @param {Object} props
 * @param {'sm'|'md'|'lg'} [props.size='md']
 * @param {React.ReactNode} [props.icon]
 * @param {string} props.title
 * @param {string} [props.description]
 * @param {string} [props.actionLabel]
 * @param {Function} [props.onAction]
 * @param {string} [props.className]
 */
const EmptyState = React.forwardRef(({
  className,
  size,
  icon,
  title,
  description,
  actionLabel,
  onAction,
  children,
  ...props
}, ref) => {
  const IconComponent = icon || FaInbox;

  return (
    <div
      ref={ref}
      className={cn(emptyStateVariants({ size }), className)}
      {...props}
    >
      {typeof IconComponent === 'function' ? (
        <IconComponent className="h-12 w-12 text-[var(--text-tertiary)]" />
      ) : (
        <div className="h-12 w-12 text-[var(--text-tertiary)]">
          {IconComponent}
        </div>
      )}

      <h3 className="text-lg font-semibold text-[var(--text-primary)]">
        {title}
      </h3>

      {description && (
        <p className="max-w-sm text-[var(--text-secondary)]">
          {description}
        </p>
      )}

      {actionLabel && onAction && (
        <Button onClick={onAction} className="mt-2">
          {actionLabel}
        </Button>
      )}

      {children}
    </div>
  );
});

EmptyState.displayName = 'EmptyState';

export { EmptyState };
export default EmptyState;
